import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import Modal from "../../layouts/Modal";
import { IPart } from "../../models/Part";
import classNames from "../../utils/classNames";
import { scanActionCode } from "../../utils/scanActions";
import ScanQrCode from "../shared/ScanQrCode";

interface ScanPartActionModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  part: IPart | null;
  /** Shown under the part title so the operator can sanity-check the donor. */
  vehicleVrm?: string | null;
  vehicleTitle?: string | null;
  /** The Station Manager station this job is open at, if any. */
  stationName?: string | null;
  onRemove: () => void;
  onDamage: () => void;
  onMissing: () => void;
  onNotAccessible: () => void;
  onNote: () => void;
}

// Shown when a part is scanned on the job. Every action has a QR code so the
// operator can drive the whole flow from the scanner: scan the same part again
// (or the Removed code) to remove it, or scan a code to mark it damaged /
// missing / not accessible, add a note, or cancel. The scan handling itself
// lives in the caller (JobWorkspace's scanner override) - this component is
// only the display.
export default function ScanPartActionModal({
  open,
  setOpen,
  part,
  vehicleVrm,
  vehicleTitle,
  stationName,
  onRemove,
  onDamage,
  onMissing,
  onNotAccessible,
  onNote,
}: ScanPartActionModalProps) {
  const { t } = useTranslation("jobs");

  const context = [
    part?.tagNumber ? t("Tag {{tag}}", { tag: part.tagNumber }) : null,
    vehicleVrm,
    vehicleTitle,
    stationName,
  ].filter((v): v is string => !!v);

  const tileButton =
    "inline-flex w-full items-center justify-center rounded-lg px-4 py-4 text-center text-xl font-bold uppercase leading-tight tracking-wide text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500";

  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-3xl"}>
      {/* Header: what was scanned, with the cancel code in the corner where the
          layout's close icon sits. */}
      <div className="flex items-start justify-between gap-4 pr-8">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Scanned Part")}</p>
          <h3 className="mt-1 truncate text-2xl font-semibold text-gray-900">
            {part?.title ?? t("Part")}
          </h3>
          {context.length > 0 && (
            <p className="mt-1 text-sm text-gray-500">{context.join(" · ")}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-center gap-2">
          <ScanQrCode value={scanActionCode("cancel")} alt={t("Cancel QR code")} size={72} className="h-16 w-16" />
          <button
            type="button"
            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500"
            onClick={() => setOpen(false)}
          >
            {t("Cancel")}
          </button>
        </div>
      </div>

      {/* Primary action: removal. The part's own tag also confirms this. */}
      <div className="mt-5 flex items-center gap-4 rounded-lg border border-green-200 bg-green-50 p-4">
        <ScanQrCode value={scanActionCode("removed")} alt={t("Removed QR code")} size={112} className="h-28 w-28 shrink-0" />
        <div className="min-w-0 flex-1">
          <button
            type="button"
            className={classNames(tileButton, "bg-green-600 py-6 text-3xl hover:bg-green-700")}
            onClick={onRemove}
          >
            {t("Removed")}
          </button>
          <p className="mt-2 text-sm font-medium text-green-700">
            {t("Scan the part again, scan the code or tap the button to confirm.")}
          </p>
        </div>
      </div>

      {/* Reasons the part isn't coming off. */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="flex flex-col items-center gap-3">
          <ScanQrCode value={scanActionCode("damaged")} alt={t("Damaged QR code")} size={96} className="h-24 w-24" />
          <button type="button" className={classNames(tileButton, "bg-red-600 hover:bg-red-700")} onClick={onDamage}>
            {t("Damaged")}
          </button>
        </div>
        <div className="flex flex-col items-center gap-3">
          <ScanQrCode value={scanActionCode("missing")} alt={t("Missing QR code")} size={96} className="h-24 w-24" />
          <button type="button" className={classNames(tileButton, "bg-orange-600 hover:bg-orange-700")} onClick={onMissing}>
            {t("Missing")}
          </button>
        </div>
        <div className="flex flex-col items-center gap-3">
          <ScanQrCode value={scanActionCode("notaccessible")} alt={t("Not Accessible QR code")} size={96} className="h-24 w-24" />
          <button type="button" className={classNames(tileButton, "bg-amber-600 hover:bg-amber-700")} onClick={onNotAccessible}>
            {t("Not Accessible")}
          </button>
        </div>
      </div>

      {/* Note: the one action that needs the keyboard. */}
      <div className="mt-5 flex items-center gap-4 border-t border-gray-200 pt-4">
        <ScanQrCode value={scanActionCode("note")} alt={t("Add note QR code")} size={72} className="h-16 w-16 shrink-0" />
        <button
          type="button"
          className="inline-flex shrink-0 items-center justify-center rounded-lg bg-primary-700 px-6 py-4 text-base font-semibold text-white shadow-sm hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500"
          onClick={onNote}
        >
          {t("Add note")}
        </button>
        <p className="text-sm text-gray-500">
          {t("These codes are the same for every part, so they can be printed once and kept at the bench - the scanner never has to leave your hand.")}
        </p>
      </div>
    </Modal>
  );
}
